import {
  clearAuthUser,
  getCurrentUser,
  getUserIdFromAccessToken,
  recoverSessionFromOAuthCheckpointIfNeeded,
} from "../lib/authSession";

const REFRESH_PATH = "/api/users/login/refresh/";

/**
 * API origin for fetch(). Must match the browser hostname (localhost vs 127.0.0.1)
 * so httpOnly refresh cookies (SameSite=Lax) are sent on impersonate / logout / refresh.
 * Opening http://localhost:3000 while the API is hardcoded to 127.0.0.1:8000 breaks that.
 */
function getApiBaseUrl() {
  const env =
    typeof process !== "undefined" && process.env.REACT_APP_API_BASE_URL;
  if (env && String(env).trim()) {
    return String(env).trim().replace(/\/$/, "");
  }
  if (typeof window !== "undefined" && window.location?.hostname) {
    const h = window.location.hostname;
    if (h === "localhost" || h === "127.0.0.1") {
      return `http://${h}:8000`;
    }
    // Production: same-origin relative URLs. nginx serves the app and proxies
    // /api + /admin to the backend on the same domain, so no host is needed.
    return "";
  }
  return "http://127.0.0.1:8000";
}

const AUTH_TOKEN_KEYS = ["repman_access_token", "access_token", "token"];
const COMPANY_ID_KEYS = ["repman_company_id", "company_id"];

function getAuthToken() {
  if (typeof window === "undefined") return "";
  for (const key of AUTH_TOKEN_KEYS) {
    const value = window.localStorage.getItem(key);
    if (value) return value;
  }
  return "";
}

function setAuthToken(accessToken) {
  if (typeof window === "undefined" || !accessToken) return;
  window.localStorage.setItem("repman_access_token", accessToken);
}

function buildUrl(path) {
  const base = getApiBaseUrl();
  if (!path) return base;
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith("/")) return `${base}${path}`;
  return `${base}/${path}`;
}

function formatApiDetail(data, status) {
  if (!data) return `HTTP ${status}`;
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.detail)) {
    return data.detail
      .map((entry) => (typeof entry === "string" ? entry : entry?.msg || ""))
      .filter(Boolean)
      .join(" ");
  }
  if (data.code === "token_not_valid") {
    return "Your session expired. Please log in again.";
  }
  return `HTTP ${status}`;
}

let refreshInFlight = null;

async function refreshAccessToken() {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const response = await fetch(buildUrl(REFRESH_PATH), {
      method: "POST",
      credentials: "include",
    });
    const data = await response
      .json()
      .catch(() => (response.ok ? {} : { detail: "Refresh failed" }));

    if (!response.ok) {
      const message = formatApiDetail(data, response.status);
      throw new Error(message);
    }

    const access = data?.access;
    if (!access) {
      throw new Error("Refresh response did not include an access token.");
    }

    const refreshedUserId = data?.user_id ?? getUserIdFromAccessToken(access);
    if (recoverSessionFromOAuthCheckpointIfNeeded(refreshedUserId)) {
      const restored = getAuthToken();
      if (restored) return restored;
    }

    const storedUserId = getCurrentUser()?.id;
    if (
      storedUserId != null &&
      refreshedUserId != null &&
      Number(storedUserId) !== Number(refreshedUserId)
    ) {
      throw new Error("Session account mismatch after refresh. Please log in again.");
    }

    setAuthToken(access);

    const tokenUserId = getUserIdFromAccessToken(access);
    if (
      storedUserId != null &&
      tokenUserId != null &&
      Number(storedUserId) !== Number(tokenUserId)
    ) {
      if (recoverSessionFromOAuthCheckpointIfNeeded(tokenUserId)) {
        const restored = getAuthToken();
        if (restored) return restored;
      }
      throw new Error("Session account mismatch after refresh. Please log in again.");
    }

    return access;
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

async function fetchWithAuth(method, path, options = {}) {
  const { auth = false, headers = {}, body, _retried = false } = options;
  const finalHeaders = { ...headers };

  if (auth) {
    const token = getAuthToken();
    if (token) {
      finalHeaders.Authorization = `Bearer ${token}`;
    }
  }

  const payload =
    body !== undefined && body !== null ? JSON.stringify(body) : undefined;
  if (payload && !finalHeaders["Content-Type"]) {
    finalHeaders["Content-Type"] = "application/json";
  }

  const response = await fetch(buildUrl(path), {
    method,
    headers: finalHeaders,
    body: payload,
    credentials: "include",
  });

  const data = await response
    .json()
    .catch(() => (response.ok ? {} : { detail: "Request failed" }));

  const isAuthFailure =
    auth &&
    (response.status === 401 ||
      (response.status === 403 && data?.code === "token_not_valid"));

  if (isAuthFailure && !_retried && path !== REFRESH_PATH) {
    try {
      await refreshAccessToken();
      return fetchWithAuth(method, path, { ...options, _retried: true });
    } catch {
      clearAuthStorage();
      const error = new Error(
        "Your session expired. Please log in again."
      );
      error.status = 401;
      error.data = data;
      error.sessionExpired = true;
      throw error;
    }
  }

  if (!response.ok) {
    const error = new Error(formatApiDetail(data, response.status));
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

async function request(method, path, options = {}) {
  return fetchWithAuth(method, path, options);
}

function clearAuthStorage() {
  if (typeof window === "undefined") return;
  for (const key of AUTH_TOKEN_KEYS) {
    window.localStorage.removeItem(key);
  }
  clearAuthUser();
}

async function logout() {
  const token = getAuthToken();
  try {
    if (token) {
      await fetch(buildUrl("/api/users/logout/"), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
    }
  } catch {
    // Local session is cleared even if the server call fails.
  } finally {
    clearAuthStorage();
  }
}

const api = {
  get(path, options) {
    return request("GET", path, options);
  },
  post(path, body, options) {
    return request("POST", path, { ...options, body });
  },
  patch(path, body, options) {
    return request("PATCH", path, { ...options, body });
  },
  put(path, body, options) {
    return request("PUT", path, { ...options, body });
  },
  delete(path, options) {
    return request("DELETE", path, options);
  },
  logout,
  clearAuthStorage,
};

export default api;
